import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../context/TranslationContext';
import { destroySessionId } from '../utils/trackingParams';
import {
  IDLE_EXPIRY_ENABLED,
  REFRESH_NOTICE_KEY,
  buildFreshSearchUrl,
  getIdleLimitMs,
  getIdleMs,
  isPaymentInProgress,
  markActivity,
} from '../utils/sessionExpiry';

// Only these two routes are guarded. A finished booking (/confirmation,
// /booking-details), the payment page and the group pages are never touched.
const SEARCH_PATH = '/';
const CHECKOUT_PATH = '/checkout-page';

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart', 'mousemove', 'wheel'];

// How long the "we refreshed your search" notice stays on screen.
const NOTICE_DURATION_MS = 5000;

/**
 * Sends an idle guest back to a fresh search so they never book on stale rates.
 * Renders the small "we refreshed your search" notice after it has happened.
 */
const SessionExpiryGuard = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [showNotice, setShowNotice] = useState(false);
  const firedRef = useRef(false);

  const path = location.pathname;
  const isGuarded = path === SEARCH_PATH || path === CHECKOUT_PATH;

  // Show the notice once, on the search page we landed back on. Reading the
  // flag and running the auto-hide timer are separate effects on purpose:
  // StrictMode invokes an effect twice, and the second pass no longer sees the
  // (already consumed) flag — a timer created here would be cleared and never
  // recreated, leaving the notice on screen.
  useEffect(() => {
    if (!IDLE_EXPIRY_ENABLED) return;
    let stored = null;
    try { stored = sessionStorage.getItem(REFRESH_NOTICE_KEY); } catch (e) { /* private mode */ }
    if (!stored) return;
    try { sessionStorage.removeItem(REFRESH_NOTICE_KEY); } catch (e) { /* ignore */ }
    setShowNotice(true);
  }, []);

  useEffect(() => {
    if (!showNotice) return undefined;
    const timer = setTimeout(() => setShowNotice(false), NOTICE_DURATION_MS);
    return () => clearTimeout(timer);
  }, [showNotice]);

  const expire = useCallback(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    try { sessionStorage.setItem(REFRESH_NOTICE_KEY, '1'); } catch (e) { /* ignore */ }

    // This much inactivity is a new visit: drop the tracking session so the
    // next load starts a fresh one (the old id is not carried in the URL either).
    destroySessionId();

    // Always a full load to a clean search URL — not a client-side navigate —
    // so the guest also picks up the current build, not just fresh prices.
    window.location.assign(buildFreshSearchUrl(searchParams));
  }, [searchParams]);

  useEffect(() => {
    // Off means off: no listeners, no interval, no redirect.
    if (!IDLE_EXPIRY_ENABLED || !isGuarded) return undefined;

    const limitMs = getIdleLimitMs(searchParams);
    const onActivity = () => markActivity();

    const check = () => {
      if (isPaymentInProgress()) return;      // never interrupt a payment
      if (getIdleMs() < limitMs) return;
      expire();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };

    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, onActivity, { passive: true })
    );
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', check);
    window.addEventListener('pageshow', check);
    const interval = setInterval(check, 60 * 1000);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', check);
      window.removeEventListener('pageshow', check);
      clearInterval(interval);
    };
  }, [isGuarded, searchParams, expire]);

  if (!showNotice) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[10001] flex justify-center px-3 pointer-events-none"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
      role="status"
      aria-live="polite"
    >
      <div className="pointer-events-auto flex items-center gap-2.5 max-w-md w-full sm:w-auto rounded-xl bg-white shadow-xl ring-1 ring-black/10 px-4 py-3">
        <svg className="w-4 h-4 shrink-0 text-emerald-600" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
             aria-hidden="true">
          <path d="M21 12a9 9 0 1 1-2.64-6.36" />
          <polyline points="21 3 21 9 15 9" />
        </svg>
        <span className="text-sm text-gray-700 leading-snug">
          {t('common.sessionRefreshed')}
        </span>
        <button
          type="button"
          onClick={() => setShowNotice(false)}
          className="ml-auto -mr-1 p-1.5 text-gray-400 hover:text-gray-600"
          aria-label={t('common.close')}
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default SessionExpiryGuard;
